import { useExamEventDetails } from '@exam-center/exam-event-management';
import { GetExamEventByIdDocument, useUpdateExamEventMutation } from '@exam-center/graphql';
import {
  Dialog,
  Form,
  DialogContent,
  DialogActions,
  Button,
  FormButton,
  Divider,
  DialogTitle,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useAddProctorSearchParams } from '../../hooks';
import { AddProctorModalDetailsFormFields } from '../add-proctor-modal-details-form-fields';

import { ProctorType } from './types';

interface AddProctorModalProps {
  isOpen: boolean;
  onClose: () => void;
  proctorType?: ProctorType;
}

const baseAddProctorFormSchema = z.object({
  proctorType: z.nativeEnum(ProctorType),
  proctorIds: z.array(z.string()),
});

type addProctorFormType = z.infer<typeof baseAddProctorFormSchema>;

export default function AddProctorModal(props: Readonly<AddProctorModalProps>) {
  const { isOpen, onClose, proctorType = ProctorType.Proctor_1_And_Proctor_2 } = props;
  const { examEvent } = useExamEventDetails();
  const { showSnackbar } = useSnackbar();
  const [, { changeSearch, changePageNumber, changePageSize }] = useAddProctorSearchParams();
  const formContext = useForm<addProctorFormType>({
    defaultValues: {
      proctorType,
      proctorIds: [],
    },
    resolver: zodResolver(baseAddProctorFormSchema),
  });

  const selectedProctor = formContext.watch('proctorIds');
  const handleSelectRow = (selectedProctor: string[] | undefined) => {
    if (selectedProctor) {
      formContext.setValue('proctorIds', selectedProctor, { shouldDirty: true });
    } else {
      formContext.reset();
    }
  };

  const handleClose = () => {
    formContext.resetField('proctorType', { defaultValue: ProctorType.Proctor_1_And_Proctor_2 });
    formContext.reset();
    changeSearch('');
    changePageNumber(0);
    changePageSize(10);
    onClose();
  };

  const [updateExamEvent] = useUpdateExamEventMutation({
    onCompleted: () => {
      handleClose();
      showSnackbar({ message: 'เพิ่มผู้คุมสอบเรียบร้อยแล้ว', variant: 'success' });
    },
    onError: () => {
      showSnackbar({ message: 'ไม่สามารถเพิ่มผู้คุมสอบได้', variant: 'error' });
    },
    awaitRefetchQueries: true,
    refetchQueries: [GetExamEventByIdDocument],
  });

  const onSubmit = async (data: addProctorFormType) => {
    const existingProctorIds = examEvent.proctors?.map((proctor) => proctor.id) ?? [];
    const allProctorIds = [...existingProctorIds, ...data.proctorIds];

    await updateExamEvent({
      variables: {
        updateExamEventInput: {
          id: examEvent.id,
          data: {
            proctorIds: allProctorIds,
          },
        },
      },
    });
  };

  return (
    <Dialog open={isOpen}>
      <FormProvider {...formContext}>
        <Form onSubmit={formContext.handleSubmit(onSubmit)}>
          <DialogTitle onClose={handleClose}>เพิ่มผู้คุมสอบ</DialogTitle>
          <Divider />
          <DialogContent sx={{ p: (theme) => theme.spacing(6) }}>
            <AddProctorModalDetailsFormFields
              onSelectRow={handleSelectRow}
              totalSelectedProctors={selectedProctor?.length ?? 0}
              proctorTypes={formContext.watch('proctorType')}
              proctorIds={selectedProctor}
            />
          </DialogContent>
          <Divider />
          <DialogActions>
            <Button size='large' variant='outlined' color='secondary' onClick={handleClose}>
              ยกเลิก
            </Button>
            <FormButton
              label='เพิ่มผู้คุมสอบ'
              disabled={selectedProctor.length === 0}
              size='large'
            />
          </DialogActions>
        </Form>
      </FormProvider>
    </Dialog>
  );
}
